import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Phone, Mail as MailIcon, MapPin, Clock } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import { submitContactForm } from "../lib/api";

// iter-139 — dedicated Contact page, mirrors steeltooth.ca's contact
// section (image 1 from the user's Sep 3 2026 message). Dark hero +
// dark form + service dropdown, keyed against the same /api/contact
// endpoint used by the landing page's inline form.

const SERVICES = [
    "Excavation",
    "Demolition",
    "Septic — inspection",
    "Septic — design & install",
    "Site services",
    "Other",
];

export default function ContactPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    useEffect(() => { /* iter-166 — auto-redirect removed; user can browse freely */ }, [user, navigate]);

    const [c, setC] = useState({
        name: "", email: "", phone: "", service: "", message: "", website: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const setField = (k) => (e) => setC((prev) => ({ ...prev, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        if (!c.name.trim() || !c.email.trim() || !c.message.trim()) {
            toast.error("Name, email, and message are required.");
            return;
        }
        setSending(true);
        try {
            const messageWithService = c.service
                ? `[${c.service}] ${c.message}`
                : c.message;
            await submitContactForm({ ...c, message: messageWithService });
            setSent(true);
            setC({ name: "", email: "", phone: "", service: "", message: "", website: "" });
            toast.success("Message sent — we'll be in touch soon.");
        } catch (err) {
            toast.error(formatApiError(err) || "Could not send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <MarketingShell testid="contact-page">
            <section
                className="hero-dark relative h-[46vh] min-h-[340px] flex items-end text-white"
                data-testid="contact-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(/images/services/demolition.jpg)" }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/60 to-slate-900/85" aria-hidden="true" />
                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pb-10">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A3FF]">Top Excavation Company in Barrie</p>
                    <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        Contact Steeltooth<br />Contracting
                    </h1>
                </div>
            </section>

            <section className="hero-dark bg-[#0A1128] text-slate-200 py-14">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-white uppercase tracking-tight">Talk to us today</h2>

                    <form
                        onSubmit={handleSubmit}
                        className="mt-6 space-y-4"
                        data-testid="contact-form"
                        noValidate
                    >
                        <Field id="cn" label="Name" testid="contact-name" required>
                            <Input
                                id="cn"
                                value={c.name}
                                onChange={setField("name")}
                                placeholder="Name"
                                data-testid="contact-name"
                                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-12"
                                required
                            />
                        </Field>
                        <Field id="ce" label="Email" testid="contact-email" required>
                            <Input
                                id="ce"
                                type="email"
                                inputMode="email"
                                value={c.email}
                                onChange={setField("email")}
                                placeholder="Email"
                                data-testid="contact-email"
                                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-12"
                                required
                            />
                        </Field>
                        <Field id="cp" label="Telephone" testid="contact-phone">
                            <Input
                                id="cp"
                                inputMode="tel"
                                value={c.phone}
                                onChange={setField("phone")}
                                placeholder="Telephone"
                                data-testid="contact-phone"
                                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-12"
                            />
                        </Field>
                        <Field id="cs" label="Service of interest">
                            <select
                                id="cs"
                                value={c.service}
                                onChange={setField("service")}
                                data-testid="contact-service"
                                className="w-full h-12 bg-slate-900 border border-slate-700 text-white rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                            >
                                <option value="">— Select Service of Interest —</option>
                                {SERVICES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </Field>
                        <Field id="cm" label="Tell us about your project" testid="contact-message" required>
                            <Textarea
                                id="cm"
                                value={c.message}
                                onChange={setField("message")}
                                placeholder="Tell us about your project"
                                rows={5}
                                data-testid="contact-message"
                                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                                required
                            />
                        </Field>

                        {/* Honeypot */}
                        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                            <label>Website<input tabIndex={-1} autoComplete="off" value={c.website} onChange={setField("website")} /></label>
                        </div>

                        <Button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit"
                            className="w-full h-12 bg-slate-300 hover:bg-white text-slate-900 font-bold uppercase tracking-wider"
                        >
                            {sending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                            ) : (
                                <><Send className="w-4 h-4 mr-2" />Send</>
                            )}
                        </Button>

                        <p className="text-xs text-slate-400 text-center" data-testid="contact-status">
                            {sent
                                ? "Thanks — we'll be in touch shortly."
                                : "Steeltooth Contracting will never sell your information to a third-party provider."}
                        </p>
                    </form>
                </div>
            </section>

            <section className="hero-dark bg-black text-slate-300 py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                        <h3 className="text-xl font-bold text-white uppercase tracking-wide">Contact us</h3>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><a href="tel:7053334376" className="hover:text-white">705-333-4376</a></li>
                            <li className="flex items-start gap-3"><MailIcon className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><a href="mailto:info@steeltooth.ca" className="hover:text-white break-all">info@steeltooth.ca</a></li>
                            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#00A3FF]" />3553 20 Sideroad, Innisfil ON</li>
                            <li className="flex items-start gap-3"><Clock className="w-4 h-4 mt-0.5 text-[#00A3FF]" />7 AM – 5 PM Mon–Fri</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white uppercase tracking-wide">Connect with us</h3>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li><a href="https://instagram.com/steeltoothcontracting" target="_blank" rel="noreferrer" className="hover:text-white">Instagram</a></li>
                            <li><a href="https://facebook.com/steeltoothcontracting" target="_blank" rel="noreferrer" className="hover:text-white">Facebook</a></li>
                        </ul>
                    </div>
                </div>
            </section>
        </MarketingShell>
    );
}

function Field({ id, label, testid, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                {label}{required && <span className="text-red-400 ml-1">*</span>}
            </Label>
            {children}
        </div>
    );
}
